import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackTop from "@/components/feature/BackTop";
import { usePageMeta } from "@/hooks/usePageMeta";
import { site } from "@/mocks/content";

const principles = [
  {
    icon: "ri-customer-service-2-fill",
    t: "多源交叉核实",
    d: "独家线索至少需两名独立信源或一手材料支撑，编辑部内部交叉质询后才会进入写作流程。",
  },
  {
    icon: "ri-chat-history-line",
    t: "传闻与事实分清",
    d: "凡是未经证实的猜测，一律标注为“传闻”或“知情人士说法”，绝不混同为既定事实。",
  },
  {
    icon: "ri-refresh-line",
    t: "公开更正的勇气",
    d: "发现报道偏差时，第一时间核实并发布更正或撤稿说明，对读者的信任负责到底。",
  },
  {
    icon: "ri-shield-user-line",
    t: "保护信源与隐私",
    d: "在合法合规的前提下保护线人安全，不公开无关个人信息，避免二次伤害。",
  },
];

const flow = [
  { step: "01", t: "线索收集", d: "来自线人、邮件与公开信息的多渠道线索先归档。" },
  { step: "02", t: "交叉核实", d: "比对时间线、通稿与第三方资料，剔除无法验证的信息。" },
  { step: "03", t: "成稿编辑", d: "由栏目编辑按事实、平衡、可读三重标准打磨。" },
  { step: "04", t: "发布与追踪", d: "发布后持续跟进，如有新证据及时更新或更正。" },
];

const schemaOrg = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: site.name,
  alternateName: "今日新瓜娱乐资讯",
  description: site.desc,
  email: site.email,
  slogan: site.slogan,
  knowsAbout: ["独家内幕", "娱乐八卦", "深度报道", "综艺热播"],
};

export default function AboutPage() {
  usePageMeta(
    "关于今日新瓜 - 独家内幕与爆料平台的定位与内容原则",
    "了解今日新瓜的品牌定位、发展理念与内容原则：我们以多源核实为基础，专注文娱独家内幕、独家爆料与深度栏目，坚持理性吃瓜、尊重原创。",
  );
  return (
    <>
      <JsonLd data={schemaOrg} />
      <Navbar />
      <main>
        {/* 首屏 */}
        <section className="relative overflow-hidden border-b border-background-200 bg-primary-700 text-background-50">
          <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-60 w-60 rounded-full bg-foreground-950/40 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent-300">
              <i className="ri-flashlight-fill" /> About {site.name}
            </p>
            <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-tight md:text-5xl">
              我们只做一件事：<br className="hidden md:block" />
              把内幕讲得明明白白
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
              今日新瓜诞生于一次想“把瓜吃明白”的冲动。与其跟着热搜人云亦云，不如回到事件本身，
              用一套可追溯的核实方法，把独家内幕与深度栏目做给真正愿意花时间阅读的人。
            </p>
          </div>
        </section>

        {/* 数字 */}
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-4 md:grid-cols-4 md:px-6">
            {[
              { n: "6", l: "垂直栏目", i: "ri-grid-fill" },
              { n: "20+", l: "累计独家报道", i: "ri-article-line" },
              { n: "2×", l: "每日定时更新", i: "ri-time-line" },
              { n: "100%", l: "线索需核实", i: "ri-shield-check-line" },
            ].map((s) => (
              <div key={s.l} className="flex items-center gap-3 border-background-200 px-2 py-6 md:px-6">
                <i className={`${s.i} text-2xl text-primary-600`} />
                <div>
                  <p className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">
                    {s.n}
                  </p>
                  <p className="text-xs text-foreground-500 md:text-sm">{s.l}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-2 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-quill-pen-fill" /> Our Principles
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              一份瓜要端得稳，先得守得住这些底线
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              吃瓜的爽感来自真实，而真实需要代价。我们用一套朴素但严格的内容原则，守住每一次发声的分寸。
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div
                key={p.t}
                className="rounded-2xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-200"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-700">
                  <i className={p.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                  {p.t}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 流程 */}
        <section className="border-y border-background-200 bg-primary-50/50">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-700">
                  <i className="ri-scales-3-fill" /> Editorial Flow
                </p>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
                  一篇独家内容是怎样诞生的
                </h2>
              </div>
              <Link
                to="/category/exclusive"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-700 hover:text-primary-800"
              >
                去读独家内容 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              {flow.map((f, i) => (
                <div
                  key={f.step}
                  className={`relative rounded-2xl border border-background-200 bg-background-50 p-6 ${
                    i % 2 === 1 ? "md:mt-6" : ""
                  }`}
                >
                  <span className="font-heading text-3xl font-black text-primary-200">
                    {f.step}
                  </span>
                  <h3 className="mt-2 font-heading text-base font-bold text-foreground-900">
                    {f.t}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {f.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 联系 CTA */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col items-center rounded-3xl border border-background-200 bg-background-50 p-8 text-center md:p-12">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-100 text-2xl text-accent-700">
              <i className="ri-mail-send-line" />
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-900">
              有独家线索，或想加入我们？
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              无论是爆料、纠错、转载授权还是商务合作，都欢迎写信给今日新瓜编辑部，我们会在工作日尽快回复。
            </p>
            <a
              href={`mailto:${site.email}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-mail-fill" /> {site.email}
            </a>
            <p className="mt-3 text-xs text-foreground-400">
              建议在邮件中写明事由与可联系方式，便于编辑部快速处理。
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}